import { ChevronsUpDown, LogOut, UserPen, UserCog2, Moon, Sun } from "lucide-react";
import { useTheme } from "@shared-kernel/primary/layout/theme-provider";
import { Avatar, AvatarFallback } from "@components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@components/ui/sidebar";
import type { UserVM } from "@shared-kernel/application/ports/shared/shared-repository";
import { Link } from "react-router-dom";
import { useState } from "react";
import { getInitials } from "@utils/formatting";
import { ADMIN_URLS, ROLE_BASED_URLS } from "src/routes";
import type { ROLES } from "@user-management-context/shared/domain/types/enums/roles";
import { useSession } from "@user-management-context/primary/hooks/use-session";
import { ImpersonateUserDialog } from "./impersonate-user-dialog";

interface Props {
  user: UserVM;
  onLogout: () => void;
  onImpersonate: (userId: string, activeRole: ROLES) => void;
}

export function NavUser({ user, onLogout, onImpersonate }: Props) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { isMobile, setOpenMobile } = useSidebar();
  const { theme, setTheme } = useTheme();
  const { activeRole } = useSession();
  const isAdmin = user.isAdmin;

  const name = `${user.name} ${user.lastName}`;
  const initials = getInitials(name);

  const profileUrl = (() => {
    if (activeRole) return ROLE_BASED_URLS[activeRole].profile;
    if (user.isAdmin) return ADMIN_URLS.profile;
    const firstRole = user.roles[0]?.role;
    return firstRole ? ROLE_BASED_URLS[firstRole].profile : ADMIN_URLS.profile;
  })();

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
                <Avatar className="size-8 rounded-lg">
                  <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{name}</span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
                <ChevronsUpDown className="ml-auto size-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              side={isMobile ? "bottom" : "right"}
              align="end"
              sideOffset={4}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar className="size-8 rounded-lg">
                    <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{name}</span>
                    <span className="truncate text-xs">{user.email}</span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <Link to={profileUrl} onClick={() => isMobile && setOpenMobile(false)}>
                  <DropdownMenuItem>
                    <UserPen className="mr-2" />
                    Profil
                  </DropdownMenuItem>
                </Link>
                <DropdownMenuItem
                  onClick={() => {
                    setTheme(theme === "light" ? "dark" : "light");
                    if (isMobile) setOpenMobile(false);
                  }}
                >
                  {theme === "light" ? (
                    <>
                      <Moon className="mr-2" />
                      Mode sombre
                    </>
                  ) : (
                    <>
                      <Sun className="mr-2" />
                      Mode clair
                    </>
                  )}
                </DropdownMenuItem>
              </DropdownMenuGroup>
              {isAdmin && (
                <DropdownMenuGroup>
                  <DropdownMenuItem
                    onClick={() => {
                      setIsDropdownOpen(false);
                      setIsDialogOpen(true);
                    }}
                  >
                    <UserCog2 className="mr-2" />
                    Changer d'utilisateur
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem
                  onClick={() => {
                    if (isMobile) setOpenMobile(false);
                    onLogout();
                  }}
                >
                  <LogOut className="mr-2" />
                  Se déconnecter
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>

      {isAdmin && (
        <ImpersonateUserDialog
          isOpen={isDialogOpen}
          onOpenChange={open => {
            setIsDialogOpen(open);
            if (!open) {
              setIsDropdownOpen(false);
            }
          }}
          onImpersonate={onImpersonate}
        />
      )}
    </>
  );
}
